import { useEffect } from 'react';

/**
 * WebKit-only fallback for the native browser Back button, which React Router's
 * useBlocker doesn't reliably hold there: the POP commits before the block takes
 * effect, so the confirm modal only flashes — or the user leaves a dirty form
 * with no guard at all. Blink/Gecko hold POP fine, so callers must arm this only
 * on WebKit; running it alongside useBlocker's own POP handling (as Blink does)
 * makes the two race on the same popstate.
 *
 * It arms a history "sentinel" — a duplicate of the current URL — so a back press
 * pops the duplicate (the URL stays the same, no real navigation) and calls
 * `onBack` instead. The sentinel is re-pushed synchronously at the top of every
 * popstate so it stays armed across repeated presses. `onBack` should trigger
 * normal in-app navigation, which useBlocker then handles the same way as the
 * in-app Back/Cancel buttons.
 *
 * Only arm this while the form is genuinely dirty (`when`). A pristine form must
 * never arm it, or Back gets trapped on a clean page.
 */
export function useBackButtonGuard({ when, onBack }) {
  useEffect(() => {
    if (!when) return undefined;

    window.history.pushState(null, '', window.location.href);
    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
      onBack();
    };
    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, [when, onBack]);
}
